import React, { useState } from 'react';
import { ClipboardCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { fieldErrorsOf } from '@/lib/forms/fieldErrors';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { AutoGrowTextarea } from '@/components/ui/auto-grow-textarea';
import { useConfirm } from '@hooks/useConfirm';
import {
  changeReviewRowSchema,
  type ChangeReviewRowValues,
} from '@/features/drbfm/schemas/changeReview.schema';
import { LoadingButton } from '../../common/LoadingButton';
import type { DRBFMCaseDesignChange } from '../../../types/drbfm';

interface DRBFMChangeReviewStepProps {
  changes: DRBFMCaseDesignChange[];
  isLoading: boolean;
  onChange: (changes: DRBFMCaseDesignChange[]) => void;
  onSubmit: (changes: DRBFMCaseDesignChange[]) => void | Promise<unknown>;
  onBack: () => void;
}

type ChangeField = keyof ChangeReviewRowValues;
type RowErrors = Partial<Record<ChangeField, string>>;

// First issue per field, so each Field shows its own message.
const rowErrorsOf = (change: DRBFMCaseDesignChange): RowErrors =>
  fieldErrorsOf(changeReviewRowSchema.safeParse(change));

const DRBFMChangeReviewStep: React.FC<DRBFMChangeReviewStepProps> = ({
  changes,
  isLoading,
  onChange,
  onSubmit,
  onBack,
}) => {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [rowErrors, setRowErrors] = useState<RowErrors[]>([]);

  const updateChange = (
    index: number,
    field: ChangeField,
    value: string,
  ) => {
    const nextChanges = changes.map((change, currentIndex) => (
      currentIndex === index ? { ...change, [field]: value } : change
    ));
    // Re-check the edited row only once a failed submit has put messages on screen.
    setRowErrors((previous) => (previous.length === 0 ? previous : previous.map((errors, currentIndex) => (
      currentIndex === index ? rowErrorsOf(nextChanges[index]) : errors
    ))));
    onChange(nextChanges);
  };

  // No RHF: the rows live in the case workflow hook (DRBFMCaseWorkflow.tsx:68-70), and
  // `changes` is what saveCaseDesignChanges posts verbatim (drbfm.service.mappers.ts:480-491).
  const handleSubmit = async () => {
    const nextErrors = changes.map(rowErrorsOf);
    setRowErrors(nextErrors);
    if (nextErrors.some((errors) => Object.keys(errors).length > 0)) return;

    await onSubmit(changes);
  };

  // onBack tears the case down entirely; nothing here is persisted and there is
  // no resume-by-case-id, so this is the last chance to keep the edits.
  const handleBack = async () => {
    if (!(await confirm({
      title: t('drbfm.confirm.backToConditions.title'),
      description: t('drbfm.confirm.backToConditions.description'),
      confirmLabel: t('drbfm.confirm.backToConditions.confirm'),
    }))) return;
    onBack();
  };

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="revy-cjk mb-2 text-xl font-medium leading-[1.5]">
          {t('drbfm.case.reviewTitle', '変更点レビュー')}
        </h2>
        <p className="revy-cjk text-sm leading-[1.6] text-muted-foreground">
          {t('drbfm.case.reviewDescription', '抽出された技術変更点をDRBFM入力として整えます。')}
        </p>
      </div>

      {changes.length === 0 ? (
        <p className="revy-cjk text-base leading-[1.6] text-muted-foreground">
          {t('drbfm.case.noChanges', '抽出された変更点はありません。')}
        </p>
      ) : (
        <div className="flex flex-col gap-5">
          {changes.map((change, index) => (
            <React.Fragment key={change.id || index}>
              {index > 0 && <Separator />}
              <div className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="revy-cjk revy-figure text-base font-normal leading-[1.5]">
                    {t('drbfm.case.changeNumber', {
                      number: index + 1,
                      defaultValue: `変更点 ${index + 1}`,
                    })}
                  </h3>
                  <Badge variant="secondary" className="font-normal">
                    {change.category}
                  </Badge>
                  {change.confidence != null && (
                    <Badge variant="outline" className="revy-figure font-normal">
                      {t('drbfm.case.confidence', {
                        value: Math.round(change.confidence * 100),
                        defaultValue: `信頼度 ${Math.round(change.confidence * 100)}%`,
                      })}
                    </Badge>
                  )}
                </div>

                <Field
                  label={t('drbfm.case.changedItem', '変更項目')}
                  error={rowErrors[index]?.itemName}
                >
                  {(control) => (
                    <Input
                      {...control}
                      value={change.itemName}
                      onChange={(event) => updateChange(index, 'itemName', event.target.value)}
                      disabled={isLoading}
                    />
                  )}
                </Field>

                <div className="flex flex-col gap-4 min-[900px]:flex-row">
                  <Field
                    label={t('drbfm.case.oldSpecification', '旧仕様')}
                    error={rowErrors[index]?.oldSpecification}
                    className="flex-1"
                  >
                    {(control) => (
                      <Input
                        {...control}
                        value={change.oldSpecification}
                        onChange={(event) => updateChange(index, 'oldSpecification', event.target.value)}
                        disabled={isLoading}
                      />
                    )}
                  </Field>
                  <Field
                    label={t('drbfm.case.newSpecification', '新仕様')}
                    error={rowErrors[index]?.newSpecification}
                    className="flex-1"
                  >
                    {(control) => (
                      <Input
                        {...control}
                        value={change.newSpecification}
                        onChange={(event) => updateChange(index, 'newSpecification', event.target.value)}
                        disabled={isLoading}
                      />
                    )}
                  </Field>
                </div>

                <Field
                  label={t('drbfm.case.technicalChange', '技術変更内容')}
                  error={rowErrors[index]?.changeDescription}
                >
                  {(control) => (
                    <AutoGrowTextarea
                      {...control}
                      rows={3}
                      value={change.changeDescription}
                      onChange={(event) => updateChange(index, 'changeDescription', event.target.value)}
                      disabled={isLoading}
                    />
                  )}
                </Field>
              </div>
            </React.Fragment>
          ))}
        </div>
      )}

      <div className="flex flex-wrap justify-between gap-2">
        <Button variant="outline" disabled={isLoading} onClick={handleBack}>
          <span className="revy-cjk">{t('drbfm.case.backToConditions', 'ケース条件に戻る')}</span>
        </Button>
        <LoadingButton
          loading={isLoading}
          loadingText={t('drbfm.case.generatingPrecheck', '事前確認を生成中')}
          disabled={changes.length === 0}
          onClick={handleSubmit}
        >
          <ClipboardCheck strokeWidth={1.9} />
          <span className="revy-cjk">{t('drbfm.case.continueToPrecheck', '事前確認へ進む')}</span>
        </LoadingButton>
      </div>
    </div>
  );
};

export default DRBFMChangeReviewStep;
